import { useQuery } from '@apollo/client/react'
import { Navigate, useParams } from 'react-router-dom'
import Spinner from '~/components/Spinner'
import { GQL_MY_SESSIONS } from '~/gql/sessions/sessions'

/**
 * Point d'entrée d'un parcours sans module désigné : `/scorm/:sessionId`.
 *
 * Cette route existait avant le passage au parcours (#139) et **doit survivre** :
 * c'est elle que portent les liens de rappel déjà partis par mail
 * (`reminders.service.ts`). Un lien mort dans une boîte ne se rattrape pas.
 *
 * Elle redirige vers le prochain module OUVERT ET NON TERMINÉ, ce qui donne au
 * lien de rappel exactement le sens qu'on veut : « reprends où tu en étais ».
 * À défaut, le premier module ouvert — un parcours entièrement terminé s'ouvre
 * donc sur sa première étape, en révision.
 */
function ScormResume() {
  const { sessionId } = useParams()
  const { data, loading } = useQuery(GQL_MY_SESSIONS, { fetchPolicy: 'cache-and-network' })

  if (loading && !data) {
    return (
      // Écran de transit des liens de rappel par mail : même correction que le
      // lecteur. Peint en dur, il envoyait un éclair presque noir à quiconque
      // clique depuis sa boîte en thème clair.
      <div className="flex min-h-dvh items-center justify-center bg-background">
        <Spinner className="size-6 text-muted-foreground" />
      </div>
    )
  }

  const session = data?.mySessions?.find(s => s.id === sessionId)
  const steps = session?.modules ?? []
  const openSteps = steps.filter(s => s.unlocked && s.module?.id)

  const target = openSteps.find(s => s.myTracking?.completionStatus !== 'COMPLETED')
    ?? openSteps[0]

  // Aucun module ouvert : parcours introuvable, non inscrit, ou entièrement
  // verrouillé (tout est programmé plus tard). La liste des formations porte
  // l'explication — dates comprises —, ce qu'un écran d'erreur ne ferait pas.
  if (!target) return <Navigate to="/my-sessions" replace />

  return <Navigate to={`/scorm/${sessionId}/${target.module!.id}`} replace />
}

export default ScormResume
